import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactGA from "react-ga4";
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../hooks/useSEO';

const LoginPage = () => {
   useSEO({
    title: "Login to Your Account | AM CARDMATE",
    description: "Sign in to your AM CARDMATE account to manage your digital business cards, view analytics, and access your profile.",
    keywords: "login, sign in, account access, user login"
  });

  useEffect(() => {
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Sign In to AM CARDMATE", 
      "description": "Access your digital business cards and manage your professional network",
      "url": "https://amcardmate.com/login", 
      "mainEntity": {
        "@type": "WebApplication",
        "name": "AM CARDMATE",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web Browser",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP"
        }
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "Action",
      "name": "User Authentication",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://amcardmate.com/account",
        "actionPlatform": [
          "http://schema.org/DesktopWebPlatform",
          "http://schema.org/MobileWebPlatform"
        ]
      }
    }
  ];
  
  schemas.forEach(schema => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  });
}, []);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [rememberMe, setRememberMe] = useState(false);
  
  const { login, signInWithGoogle, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/account';

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail');
    if (rememberedEmail) {
      setFormData(prev => ({ ...prev, email: rememberedEmail }));
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    ReactGA.event({
      category: "User",
      action: "Login Attempt",
      label: "Email/Password"
    });

    try {
      const { user, error } = await login(formData.email, formData.password);
      
      if (user && !error) {
        ReactGA.event({
          category: "User",
          action: "Login Success",
          label: "Email/Password"
        });
        
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }
        
        navigate(from, { replace: true });
      } else if (error) {
        ReactGA.event({
          category: "User",
          action: "Login Failed",
          label: error
        });
        setErrors({ general: error });
      }
    } catch (error) {
      ReactGA.event({
        category: "User",
        action: "Login Error",
        label: "Unexpected Error"
      });
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    }
    
    setLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setErrors({});

    ReactGA.event({
      category: "User",
      action: "Login Attempt",
      label: "Google OAuth"
    });

    try {
      const { user, error } = await signInWithGoogle();
      
      if (user && !error) {
        ReactGA.event({
          category: "User",
          action: "Login Success",
          label: "Google OAuth"
        });
        navigate(from, { replace: true });
      } else if (error) {
        ReactGA.event({
          category: "User",
          action: "Login Failed",
          label: "Google OAuth Error"
        });
        setErrors({ general: error });
      }
    } catch (error) {
      ReactGA.event({
        category: "User",
        action: "Login Error",
        label: "Google OAuth Unexpected"
      });
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    }
    
    setGoogleLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && !googleLoading) {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] md:w-[500px] h-[300px] sm:h-[400px] md:h-[500px] bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[300px] md:w-[400px] h-[250px] sm:h-[300px] md:h-[400px] bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
            <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl text-white shadow-lg group-hover:shadow-xl transition-all duration-300">
              <FontAwesomeIcon icon="qrcode" className="text-xl" />
            </div>
            <span className="text-h3 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">AM CARDMATE</span>
          </Link>
          
          <h2 className="text-h2 text-dark">
            Welcome back
          </h2>
          <p className="mt-2 text-body text-gray">
            Sign in to your account to continue
          </p>
        </div>

        <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8">

          {errors.general && (
            <div className="mb-4 p-3 bg-red-50 rounded-2xl">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-red-500" />
                <p className="text-body-sm text-red-600">{errors.general}</p>
              </div>
            </div>
          )}

          <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit} onKeyPress={handleKeyPress}>
            <div>
              <label htmlFor="email" className="block text-body-sm font-medium text-dark mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="envelope" className="text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-3 bg-gray-50 placeholder-gray-500 text-dark rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.email ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <p className="mt-1 text-body-sm text-red-600">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-body-sm font-medium text-dark mb-1">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <FontAwesomeIcon icon="lock" className="text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 bg-gray-50 placeholder-gray-500 text-dark rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all duration-300 text-body ${
                    errors.password ? 'ring-2 ring-red-300' : ''
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon 
                    icon={showPassword ? "eye-slash" : "eye"} 
                    className="text-gray-400 hover:text-gray-600 transition-colors" 
                  />
                </button>
              </div>
              {errors.password && <p className="mt-1 text-body-sm text-red-600">{errors.password}</p>}
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-body-sm text-dark">
                  Remember me
                </label>
              </div>

              <div className="text-body-sm">
                <Link 
                  to="/forgot-password" 
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || googleLoading}
                className="group relative w-full flex justify-center items-center py-3 px-4 text-body font-medium rounded-2xl text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center justify-center">
                  {loading ? (
                    <FontAwesomeIcon icon="spinner" className="animate-spin" />
                  ) : (
                    <>
                      <FontAwesomeIcon icon="sign-in-alt" className="mr-2" />
                      <span>Sign In</span>
                    </>
                  )}
                </div>
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-body-sm">
                <span className="px-2 bg-white text-gray">Or continue with</span>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button 
              onClick={handleGoogleSignIn}
              disabled={loading || googleLoading}
              className="w-full inline-flex justify-center items-center py-3 px-4 rounded-2xl shadow-sm bg-white text-body font-medium text-dark hover:bg-gray-50 transition-all duration-300 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-md"
            >
              <div className="flex items-center justify-center">
                {googleLoading ? (
                  <FontAwesomeIcon icon="spinner" className="animate-spin mr-2" />
                ) : (
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                <span>Continue with Google</span>
              </div>
            </button>
          </div>

          <div className="mt-6 text-center">
            <p className="text-body text-gray">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
              >
                Create one here
              </Link>
            </p>
          </div>
        </div>

        <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 sm:p-6">
          <h3 className="text-h5 text-dark mb-3">Why professionals choose AM CARDMATE:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-body-sm text-gray">
              <FontAwesomeIcon icon="zap" className="text-yellow-500" />
              <span>Instant sharing</span>
            </div>
            <div className="flex items-center gap-2 text-body-sm text-gray">
              <FontAwesomeIcon icon="chart-bar" className="text-blue-500" />
              <span>Real-time analytics</span>
            </div>
            <div className="flex items-center gap-2 text-body-sm text-gray">
              <FontAwesomeIcon icon="shield-alt" className="text-green-500" />
              <span>Secure & private</span>
            </div>
            <div className="flex items-center gap-2 text-body-sm text-gray">
              <FontAwesomeIcon icon="globe" className="text-purple-500" />
              <span>Works everywhere</span>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Link 
            to="/" 
            className="text-body-sm text-gray hover:text-dark transition-colors inline-flex items-center gap-1"
          >
            <FontAwesomeIcon icon="arrow-left" />
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;